import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, PenLine } from "lucide-react";

const sigToHtml = (sig) => {
  const s = (sig || "").trim().replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ");
  if (!s) return "";
  return /<[a-z][^>]*>/i.test(s) ? s : s.replace(/\n/g, "<br>");
};

export const EmailPreview = ({
  open,
  onOpenChange,
  subject,
  bodyHtml,
  signature,
  fromName,
  fromEmail,
  to,
  ccList,
  bccCount,
  attachments,
  onSend,
  sending,
}) => {
  const sig = sigToHtml(signature);
  // Quill émet des &nbsp; à la place des espaces : on restaure des espaces normales (comme à l'envoi)
  const cleanBody = (bodyHtml || "").replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="email-preview-dialog"
        className="max-w-2xl w-[92vw] max-h-[90vh] flex flex-col overflow-hidden rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-[#2D2C28]">
            Aperçu de l'e-mail — tel que le patient le recevra
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm space-y-1 border-b border-[#EAE6DF] pb-3">
          <p className="text-[#575349]">
            <span className="text-[#9c988f]">De :</span>{" "}
            {fromName ? `${fromName} <${fromEmail}>` : fromEmail || "—"}
          </p>
          <p className="text-[#575349]">
            <span className="text-[#9c988f]">À :</span> {to || "—"}
            {ccList?.length > 0 && (
              <span className="ml-2 text-[#9c988f]">· Cc : {ccList.join(", ")}</span>
            )}
            {bccCount > 0 && <span className="ml-2 text-[#9c988f]">· {bccCount} Cci</span>}
          </p>
          <p className="text-[#2D2C28] font-medium" data-testid="preview-subject">
            <span className="text-[#9c988f] font-normal">Objet :</span> {subject}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-w-0 max-w-full rounded-xl bg-white border border-[#EAE6DF] p-5">
          <div
            data-testid="preview-body"
            className="preview-email text-[15px] leading-relaxed text-[#2D2C28] break-words"
            dangerouslySetInnerHTML={{ __html: cleanBody }}
          />
          {sig ? (
            <div
              data-testid="preview-signature"
              className="mt-4 pt-3 border-t border-dashed border-[#EAE6DF] text-[15px] text-[#575349]"
              dangerouslySetInnerHTML={{ __html: sig }}
            />
          ) : (
            <p className="mt-4 pt-3 border-t border-dashed border-[#EAE6DF] text-xs text-[#9c988f] flex items-center gap-1.5">
              <PenLine className="w-3.5 h-3.5" /> Pas de signature configurée (Paramètres → Signature e-mail).
            </p>
          )}
        </div>

        {attachments?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((f, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 bg-[#EAE6DF]/60 text-[#575349] rounded-full px-3 py-1 text-xs"
              >
                <Paperclip className="w-3 h-3" /> {f.name}
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="back-to-edit-btn"
            className="flex-1 rounded-xl border-[#EAE6DF] text-[#575349]"
          >
            Retour à l'édition
          </Button>
          <Button
            onClick={onSend}
            disabled={sending}
            data-testid="confirm-send-btn"
            className="flex-1 rounded-xl bg-[#7B968B] hover:bg-[#6a8579] text-white"
          >
            <Send className="w-4 h-4 mr-2" /> {sending ? "Envoi…" : "Confirmer l'envoi"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
